/* eslint-env es2020 */

const LOREM_WORDS = [
  'lorem', 'ipsum', 'dolor', 'sit', 'amet', 'consectetur', 'adipiscing', 'elit',
  'sed', 'do', 'eiusmod', 'tempor', 'incididunt', 'ut', 'labore', 'et', 'dolore',
  'magna', 'aliqua', 'enim', 'ad', 'minim', 'veniam', 'quis', 'nostrud',
  'exercitation', 'ullamco', 'laboris', 'nisi', 'aliquip', 'ex', 'ea', 'commodo',
];

const PRODUCT_WORDS = [
  'linha', 'coleção', 'série', 'modelo', 'edição', 'kit', 'pack', 'essencial',
  'premium', 'studio', 'atelier', 'home', 'gourmet', 'urban', 'classic',
];

const ICONS = ['✦', '◆', '●', '▲', '■', '★', '☆', '♦', '◎', '◇', '○', '□', '△', '✿', '❖'];

export const KIND = {
  TEXT: 'text',
  TEXT_LONG: 'text_long',
  IMAGE: 'image',
  ICON: 'icon',
  COLOR: 'color',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
};

const FNV_OFFSET = 14695981039346656037n;
const FNV_PRIME = 1099511628211n;
const MASK64 = (1n << 64n) - 1n;

const hashParts = (parts) => {
  let h = FNV_OFFSET;
  parts.forEach((p) => {
    const s = String(p ?? '');
    for (let i = 0; i < s.length; i += 1) {
      h ^= BigInt(s.codePointAt(i));
      h = (h * FNV_PRIME) & MASK64;
    }
    h ^= 0n;
    h = (h * FNV_PRIME) & MASK64;
  });
  return h;
};

export class CatalogIpsum {
  constructor(seedParts = []) {
    this.seed = hashParts(seedParts);
  }

  fork(...parts) {
    return new CatalogIpsum([this.seed, ...parts]);
  }

  next() {
    this.seed = (this.seed * 6364136223846793005n + 1n) & MASK64;
    const mantissa = (this.seed >> 11n) & ((1n << 53n) - 1n);
    return Number(mantissa) / 2 ** 53;
  }

  pick(words) {
    if (!words.length) return '';
    return words[Math.floor(this.next() * words.length)];
  }

  pickN(words, minN, maxN) {
    const span = maxN - minN + 1;
    const n = minN + Math.floor(this.next() * span);
    return Array.from({ length: n }, () => this.pick(words));
  }

  text() {
    return this.pickN(LOREM_WORDS, 3, 8).join(' ');
  }

  textLong() {
    const count = 2 + Math.floor(this.next() * 2);
    return Array.from({ length: count }, () => {
      const words = this.pickN(LOREM_WORDS, 8, 16);
      const s = words.join(' ');
      return s ? `${s.charAt(0).toUpperCase()}${s.slice(1)}.` : '';
    }).join(' ');
  }

  title() {
    const words = this.next() > 0.45 ? PRODUCT_WORDS : LOREM_WORDS;
    return this.pickN(words, 2, 4)
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(' ');
  }

  word() {
    return this.pick(LOREM_WORDS);
  }

  hexColor() {
    const r = Math.floor(this.next() * 256);
    const g = Math.floor(this.next() * 256);
    const b = Math.floor(this.next() * 256);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  color() {
    const h = Math.floor(this.next() * 360);
    const s = 35 + Math.floor(this.next() * 35);
    const l = 38 + Math.floor(this.next() * 28);
    return `hsl(${h}, ${s}%, ${l}%)`;
  }

  number(minVal = 0, maxVal = 100) {
    if (maxVal <= minVal) return minVal;
    return minVal + this.next() * (maxVal - minVal);
  }

  int(minVal, maxVal) {
    if (maxVal <= minVal) return minVal;
    return minVal + Math.floor(this.next() * (maxVal - minVal + 1));
  }

  boolean() {
    return this.next() >= 0.5;
  }

  icon() {
    return ICONS[Math.floor(this.next() * ICONS.length)];
  }

  imageUrl(width = 400, height = 500) {
    const w = width > 0 ? width : 400;
    const h = height > 0 ? height : 500;
    const seedStr = this.seed.toString(16);
    return `https://picsum.photos/seed/${seedStr}/${w}/${h}`;
  }

  textForKey(key) {
    const k = String(key || '').toLowerCase();
    if (['title', 'name', 'section_title', 'cta_label', 'tag'].includes(k)) return this.title();
    if (['subtitle', 'section_subtitle', 'description', 'content', 'text', 'message', 'slogan', 'footer_note'].includes(k)) {
      return this.textLong();
    }
    return this.text();
  }
}

export const catalogIpsum = (...seedParts) => new CatalogIpsum(seedParts);

export const generateIpsum = (kind, ...seedParts) => {
  const g = catalogIpsum(...seedParts);
  switch (String(kind || KIND.TEXT).toLowerCase()) {
    case KIND.TEXT_LONG:
    case 'textlong':
    case 'long_text':
    case 'paragraph':
      return g.textLong();
    case KIND.IMAGE:
    case 'media':
    case 'img':
      return g.imageUrl(400, 500);
    case KIND.ICON:
    case 'emoji':
      return g.icon();
    case KIND.COLOR:
    case 'colour':
      return g.hexColor();
    case KIND.NUMBER:
    case 'float':
    case 'price':
      return Math.round(g.number(1, 999) * 100) / 100;
    case KIND.BOOLEAN:
    case 'bool':
      return g.boolean();
    default:
      return g.text();
  }
};

const demoSeed = (...parts) => catalogIpsum('catalog-demo', ...parts);

export const demoFieldValue = (blockId, fieldKey, fieldType) => {
  const g = demoSeed('field', blockId, fieldKey, fieldType);
  switch (fieldType) {
    case 'boolean':
      return g.boolean();
    case 'number':
      return g.int(1, 999);
    case 'color':
      return g.hexColor();
    case 'media':
      return g.imageUrl(640, 480);
    case 'textarea':
      return g.textLong();
    case 'text':
    default:
      return g.textForKey(fieldKey);
  }
};

export const demoProductItem = (blockId, fieldKey, index) => {
  const g = demoSeed('product', blockId, fieldKey, index);
  const n = index + 1;
  const price = Math.round(g.number(19.9, 899.9) * 100) / 100;
  return {
    id: `demo-product-${blockId}-${n}`,
    product_id: '',
    title: g.title(),
    display_name: g.title(),
    description: g.textLong(),
    display_description: g.textLong(),
    has_price: true,
    price,
    badge_label: g.fork('badge').title(),
    medias: [{ file_path: g.imageUrl(400, 500) }],
  };
};

export const demoItemListItem = (blockId, fieldKey, index) => {
  const g = demoSeed('item', blockId, fieldKey, index);
  return {
    title: g.title(),
    media_id: '',
    image_url: g.imageUrl(320, 240),
  };
};

export const demoProductListPreview = (count, blockId = 'products', fieldKey = 'products') =>
  Array.from({ length: count }, (_, i) => demoProductItem(blockId, fieldKey, i));

export const demoItemListPreview = (count, blockId = 'promo', fieldKey = 'items') =>
  Array.from({ length: count }, (_, i) => demoItemListItem(blockId, fieldKey, i));
